import Editor from '@monaco-editor/react';
import { useRef } from 'react';

const HandlebarsHtmlEditor = ({ readOnly = false, options: callerOptions, ...props }) => {
  const editorRef = useRef(null);

  const handleEditorDidMount = (editor, monaco) => {
    setTimeout(() => {
      editorRef.current = editor;

      // Define Handlebars language configuration
      monaco.languages.register({ id: 'handlebars-html' });

      // Configure tokenization for Handlebars + HTML
      monaco.languages.setMonarchTokensProvider('handlebars-html', {
        tokenizer: {
          root: [
            // Handlebars block helpers
            [/\{\{\s*#\w+.*?\}\}/, 'handlebars-block-start'],
            [/\{\{\s*\/\w+\s*\}\}/, 'handlebars-block-end'],

            // Handlebars expressions
            [/\{\{\{.*?\}\}\}/, 'handlebars-expression-unescaped'],
            [/\{\{.*?\}\}/, 'handlebars-expression'],

            // HTML tags
            [/<\/?[a-zA-Z][\w-]*(?:\s+[^>]*)?>/, 'html-tag'],

            // HTML attributes
            [/\s+[\w-]+(?==)/, 'html-attribute-name'],
            [/="[^"]*"/, 'html-attribute-value'],
            [/='[^']*'/, 'html-attribute-value'],

            // HTML comments
            [/<!--/, 'html-comment', '@htmlComment'],

            // Regular text
            [/[^<{]+/, 'text'],
          ],

          htmlComment: [
            [/[^-]+/, 'html-comment'],
            [/-->/, 'html-comment', '@pop'],
            [/-/, 'html-comment'],
          ],
        },
      });

      // Define color theme for Handlebars + HTML
      monaco.editor.defineTheme('handlebars-theme', {
        base: 'vs', // Use the built-in light theme as base
        inherit: true,
        rules: [
          { token: 'hbs-block-open', foreground: 'D73A49', fontStyle: 'bold' }, // Red, bold for {{#each}}
          { token: 'hbs-block-close', foreground: 'D73A49', fontStyle: 'bold' }, // Red, bold for {{/each}}
          { token: 'hbs-unescaped', foreground: 'E36209' }, // Orange for {{{variable}}}
          { token: 'hbs-expression', foreground: '6F42C1' }, // Purple for {{variable}}
          { token: 'html-tag', foreground: '22863A' }, // Green for HTML tags
          { token: 'html-comment', foreground: '6A737D', fontStyle: 'italic' }, // Gray, italic for comments
          { token: 'text', foreground: '24292E' }, // Dark gray for text
        ],
        colors: {}, // Use default colors
      });

      // Apply the theme
      monaco.editor.setTheme('handlebars-theme');

      // Custom validation function
      const validateHandlebarsHtml = () => {
        const model = editor.getModel();
        if (!model) return;

        const code = model.getValue();
        const markers = [];

        try {
          // Validate HTML structure (ignoring Handlebars for now)
          const htmlValidationErrors = validateHtmlStructure(code, model);
          markers.push(...htmlValidationErrors);

          // Validate Handlebars syntax
          const handlebarsErrors = validateHandlebarsSyntax(code, model);
          markers.push(...handlebarsErrors);
        } catch (error) {
          console.warn('Validation error:', error);
        }

        monaco.editor.setModelMarkers(model, 'handlebars-html-validation', markers);
      };

      // HTML structure validation
      const validateHtmlStructure = (code, model) => {
        const errors = [];

        // Remove Handlebars expressions temporarily for HTML validation
        const htmlOnlyCode = code.replace(/\{\{.*?\}\}/g, '');

        // Check for unclosed HTML tags (basic implementation)
        const tagStack = [];
        const selfClosingTags = [
          'br',
          'img',
          'input',
          'hr',
          'meta',
          'link',
          'area',
          'base',
          'col',
          'embed',
          'source',
          'track',
          'wbr',
        ];

        const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g;
        let match;

        while ((match = tagRegex.exec(htmlOnlyCode)) !== null) {
          const tagName = match[1].toLowerCase();
          const isClosingTag = match[0].startsWith('</');
          const isSelfClosing = selfClosingTags.includes(tagName) || match[0].endsWith('/>');

          if (isClosingTag) {
            const lastOpenTag = tagStack.pop();
            if (!lastOpenTag || lastOpenTag.name !== tagName) {
              const position = model.getPositionAt(match.index);
              errors.push({
                severity: monaco.MarkerSeverity.Error,
                startLineNumber: position.lineNumber,
                startColumn: position.column,
                endLineNumber: position.lineNumber,
                endColumn: position.column + match[0].length,
                message: `Unexpected closing tag: ${tagName}${lastOpenTag ? ` (expected: ${lastOpenTag.name})` : ''}`,
                code: 'html-tag-mismatch',
              });
            }
          } else if (!isSelfClosing) {
            tagStack.push({ name: tagName, position: match.index });
          }
        }

        // Check for unclosed tags
        tagStack.forEach((tag) => {
          const position = model.getPositionAt(tag.position);
          errors.push({
            severity: monaco.MarkerSeverity.Warning,
            startLineNumber: position.lineNumber,
            startColumn: position.column,
            endLineNumber: position.lineNumber,
            endColumn: position.column + tag.name.length + 2,
            message: `Unclosed HTML tag: ${tag.name}`,
            code: 'html-unclosed-tag',
          });
        });

        return errors;
      };

      // Handlebars syntax validation
      const validateHandlebarsSyntax = (code, model) => {
        const errors = [];

        // Check for unmatched Handlebars block helpers
        const blockStack = [];
        const blockRegex = /\{\{\s*(#|\/)?(\w+).*?\}\}/g;
        let match;

        while ((match = blockRegex.exec(code)) !== null) {
          const isClosing = match[1] === '/';
          const isOpening = match[1] === '#';
          const helperName = match[2];

          if (isOpening) {
            blockStack.push({ name: helperName, position: match.index });
          } else if (isClosing) {
            const lastBlock = blockStack.pop();
            if (!lastBlock || lastBlock.name !== helperName) {
              const position = model.getPositionAt(match.index);
              errors.push({
                severity: monaco.MarkerSeverity.Error,
                startLineNumber: position.lineNumber,
                startColumn: position.column,
                endLineNumber: position.lineNumber,
                endColumn: position.column + match[0].length,
                message: `Unexpected closing helper: ${helperName}${lastBlock ? ` (expected: ${lastBlock.name})` : ''}`,
                code: 'handlebars-block-mismatch',
              });
            }
          }
        }

        // Check for unclosed blocks
        blockStack.forEach((block) => {
          const position = model.getPositionAt(block.position);
          errors.push({
            severity: monaco.MarkerSeverity.Warning,
            startLineNumber: position.lineNumber,
            startColumn: position.column,
            endLineNumber: position.lineNumber,
            endColumn: position.column + block.name.length + 4,
            message: `Unclosed Handlebars block: ${block.name}`,
            code: 'handlebars-unclosed-block',
          });
        });

        // Check for malformed Handlebars expressions
        const malformedRegex = /\{\{[^}]*$|^[^{]*\}\}/gm;
        let malformedMatch;
        while ((malformedMatch = malformedRegex.exec(code)) !== null) {
          const position = model.getPositionAt(malformedMatch.index);
          errors.push({
            severity: monaco.MarkerSeverity.Error,
            startLineNumber: position.lineNumber,
            startColumn: position.column,
            endLineNumber: position.lineNumber,
            endColumn: position.column + malformedMatch[0].length,
            message: 'Malformed Handlebars expression',
            code: 'handlebars-malformed',
          });
        }

        return errors;
      };

      // Set up validation on content change
      const disposable = editor.onDidChangeModelContent(() => {
        // Debounce validation
        clearTimeout(editor._validationTimeout);
        editor._validationTimeout = setTimeout(validateHandlebarsHtml, 300);
      });

      // Initial validation
      setTimeout(validateHandlebarsHtml, 500);

      // Cleanup
      return () => {
        if (disposable) disposable.dispose();
        clearTimeout(editor._validationTimeout);
      };
    }, 500);
  };

  return (
    <Editor
      onMount={handleEditorDidMount}
      options={{
        minimap: { enabled: false },
        fontSize: 14,
        tabSize: 2,
        automaticLayout: true,
        wordWrap: 'on',
        lineNumbers: 'on',
        glyphMargin: true,
        folding: true,
        // Merge any caller-supplied options, then force readOnly last so an
        // outside `options` prop can never re-enable editing and defeat the
        // template edit lock. `options` is pulled out of `props` above so the
        // spread below can't clobber this merged object.
        ...callerOptions,
        readOnly,
        domReadOnly: readOnly,
      }}
      {...props}
      defaultLanguage="handlebars-html"
    />
  );
};

export default HandlebarsHtmlEditor;
